import { Component, EventEmitter, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PtsPaymentTerminal, TerminalStatus, TerminalStatusLog, TerminalStatusLogSearchEvent } from '../../pts-payment-terminal-model';
import { TranslateTerminalStatusEnumPipe } from 'src/app/shared/pipes/translate-terminal-status-enum.pipe';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, startWith, switchMap, tap } from 'rxjs';
import { PagedResult, PagingOptions } from 'src/app/shared/models/paging';
import { PageEvent } from '@angular/material/paginator';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';

@Component({
  selector: 'adm-terminal-status',
  imports: [
    TranslateTerminalStatusEnumPipe,
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatPaginatorModule,
    ReactiveFormsModule,
  ],
  templateUrl: './terminal-status-dialog.component.html',
  styleUrl: './terminal-status-dialog.component.scss',
})
export class TerminalStatusDialogComponent extends PagerBase implements OnInit {
  dialogRef = inject<MatDialogRef<TerminalStatusDialogComponent>>(MatDialogRef);
  data = inject<{
    paymentTerminal: PtsPaymentTerminal;
  }>(MAT_DIALOG_DATA);
  private ptsPaymentTerminalService = inject(PtsPaymentTerminalService);
  private snackbarService = inject(SnackbarService);

  public paymentTerminal: PtsPaymentTerminal = { ...this.data.paymentTerminal };

  public allStatuses: TerminalStatus[] = Object.values(TerminalStatus);
  public selectedStatuses: TerminalStatus[] = [];
  public terminalStatusLogs$: Observable<PagedResult<TerminalStatusLog>>;

  public noWhiteSpaceValidator(control: AbstractControl): ValidationErrors | null {
    return (control.value || '').trim().length ? null : { whitespace: true };
  }
  public formGroup: FormGroup = new FormGroup({
    note: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(2000), this.noWhiteSpaceValidator],
    }),
  });

  get isValid() {
    if (!this.note?.value) return false;
    if (this.note?.value?.trim().length < 1) return false;
    if (this.note?.value?.length > 2000) return false;
    return true;
  }

  get note() {
    return this.formGroup.controls['note'];
  }

  public statusLogCount: number;
  public currentPage: number;
  public paging: PageEvent = new PageEvent();

  private pageChangedSubject = new BehaviorSubject<PageEvent>(this.paging);
  public pagingOptions: PagingOptions;

  ngOnInit(): void {
    this.pagingOptions = {
      pageSize: this.defaultModalPageSize,
      pageSizeOptions: this.modalPageSizes,
    };
    // Clone the initial statuses so changes are discardable
    this.selectedStatuses = [...(this.paymentTerminal.statuses || [])];

    this.terminalStatusLogs$ = this.pageChangedSubject.pipe(
      startWith(this.paging),
      switchMap((paging: PageEvent) => {
        const searchEvent: TerminalStatusLogSearchEvent = {
          paging: paging,
        };
        return this.ptsPaymentTerminalService.getTerminalStatusLogs(this.paymentTerminal.identifier, searchEvent);
      }),
      tap(pagedResult => {
        // update local count/currentPage so the template can reflect it
        this.statusLogCount = pagedResult.totalCount;
        this.currentPage = pagedResult.pageIndex;
      })
    );
  }

  isStatusDisabled(status: TerminalStatus): boolean {
    return !this.selectedStatuses.includes(status);
  }

  toggleStatus(status: TerminalStatus): void {
    const index = this.selectedStatuses.indexOf(status);
    if (index > -1) {
      this.selectedStatuses.splice(index, 1);
    } else {
      this.selectedStatuses.push(status);
    }
  }

  save(): void {
    this.ptsPaymentTerminalService
      .patchPaymentTerminalStatus(this.paymentTerminal.identifier, this.selectedStatuses, this.note.value)
      .subscribe({
        next: () => {
          this.paymentTerminal.statuses = this.selectedStatuses;
          this.pageChangedSubject.next(this.paging);
          this.note.reset();
          this.snackbarService.success('Terminalstatus opdateret');
          this.dialogRef.close(this.paymentTerminal);
        },
        error: error => {
          this.snackbarService.error(`Fejl under opdatering af terminalstatus. ${error?.message}`);
        },
      });
  }

  public onPageChanged(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.paging.pageSize = event.pageSize;
    this.pageChangedSubject.next(event);
  }
}
